import React, { useCallback, useEffect, useRef, useState } from "react";
import { collectorAvatarUrl, collectorDisplayName, generatedAvatarUrl, isCollectorOnline, isCollectorVerified } from "../collectorIdentity";
import { collectorMetaLine, trustCopy } from "../mapMarkers";
import { canStartDrag, dragOffset, dragVelocity, shouldDismiss } from "../../layout/sheetDrag";
import { useDismissible } from "../../native/dismissibleStack";
import { MOTION } from "../../styles/motionTokens";
import { prefersReducedMotion } from "../../styles/particleField";
import "../styles/CollectorMap.css";

/**
 * CollectorPreviewSheet — the compact half of the collector preview.
 *
 * Two steps, one surface. This is step one: who they are, whether they are
 * around, whether anyone has traded with them, and a glance at their cards. It
 * sits ABOVE the bottom nav rather than covering it, and takes about a third of
 * the map, so the thing you were looking at is still there behind it.
 *
 * Step two is the existing UserSidebar in the existing SwipeableDrawer, which
 * the page owns — `onExpand` escalates to it. No second profile model is
 * introduced, which is why "View collection" and the `+N` tile both just open
 * the sheet the app already has.
 *
 * The sheet swallows its own drag. Without that, dragging it pans the map
 * underneath and the collector you selected slides out from under their own
 * preview.
 */
const STRIP_TILES = 4;

/**
 * A tile is one column of a STRIP_TILES-column row, and never more.
 *
 * `flex: 1` alone has no basis and no ceiling, so the row divided itself among
 * however many cards the collector happened to have: the FEWER the cards, the
 * WIDER the tile, and `aspect-ratio: 5 / 7` turned that width into 1.4x as much
 * height. Measured on the running app, a collector with one tradeable card
 * rendered a strip of 384x537.6 and a sheet 731.8px tall — 79% of a 430x932
 * screen. At 320x568 the sheet was 577.8px and its top sat at -99.8, putting
 * the collector's name and the sheet's only close button above the viewport of
 * a page that does not scroll. The height the sheet then reported through
 * onHeightChange also pushed the recenter and privacy buttons off the top of
 * the map.
 *
 * The ceiling makes the tile size a property of the row rather than of the
 * collection, so the loaded strip can never be taller than the skeleton the
 * sheet was designed around. One card still renders as one card — the row is
 * not padded with ghost tiles to fake a full binder.
 */
const TILE_MAX_WIDTH = `${100 / STRIP_TILES}%`;

/** Shared geometry for every child of the strip that is card-shaped. */
const TILE_BOX = {
  flex: 1,
  maxWidth: TILE_MAX_WIDTH,
  aspectRatio: "5 / 7",
  borderRadius: 7,
};

/** Upward travel that reads as "open this properly" rather than a stray touch. */
const EXPAND_DRAG_PX = 28;

/**
 * Controls whose own action conflicts with "drag up to expand".
 *
 * The pointer handlers live on the sheet root, so every button on the sheet is
 * inside the drag surface, and a press that began on one of these and drifted
 * EXPAND_DRAG_PX upward armed the expand as well. Close is the worst of them:
 * pointerup runs before click, so the release expanded and the click then ran
 * onClose underneath it.
 *
 * "View collection" and the +N tile are deliberately absent. Their own onClick
 * IS onExpand, so a drifted press on them already produces the intended result,
 * and suppressing it would leave nothing in its place.
 */
const EXPAND_CONFLICTING_TESTIDS = [
  "collector-preview-close",
  "collector-preview-profile",
  "collector-preview-message",
  "collector-preview-reveal-match",
];

/**
 * Did this press begin on a control whose action conflicts with the expand?
 *
 * Walks up from the press target, because a touch lands on a control's inner
 * icon — the close and message buttons each wrap their glyph in an `<svg>`.
 */
const beganOnExpandConflictingControl = (target) => {
  const owner = typeof target?.closest === "function" ? target.closest("[data-testid]") : null;
  return Boolean(owner) && EXPAND_CONFLICTING_TESTIDS.includes(owner.getAttribute("data-testid"));
};

const CARD_PLACEHOLDER = "https://placehold.co/200x280?text=Card";

/**
 * A Vault card shows the owner's own photo; everything else shows catalog art.
 *
 * `collection/ListCardModal.jsx` promises the Vault uploader their photo
 * "becomes this card's image in your Vault — the one other collectors see on
 * your profile" (the P1-42 owner decision), and this strip is the FIRST Vault
 * impression a map user gets — `MapPage` feeds it glory-first on purpose. The
 * trade/sale copy makes no such promise, framing photos as a condition
 * disclosure, so those keep catalog art.
 *
 * Keyed on the row's own `collectionType` rather than on a prop, because unlike
 * the profile grid and the full sheet's Vault shelf this strip is a MIXED list —
 * MapPage concatenates glory, trade and sale into one array.
 * `getUserCollection` strips only `notes` for non-owners, so both
 * `collectionType` and `photos` are present here.
 */
const catalogImageFor = (item) => {
  const card = item?.cardId || item || {};
  return card.images?.small || card.imageUrl || null;
};

/** The owner's photo, but only where the app promised to show it. */
const ownerImageFor = (item) =>
  (item?.collectionType === "glory" ? item?.photos?.[0]?.url : null) || null;

const cardImageFor = (item) =>
  ownerImageFor(item) || catalogImageFor(item) || CARD_PLACEHOLDER;

const CollectorPreviewSheet = ({
  collector,
  cards = [],
  cardsLoading = false,
  totalCards = 0,
  bottomPx = 96,
  onClose,
  onExpand,
  onOpenProfile,
  onMessage,
  messageBusy = false,
  /**
   * Publishes the sheet's real height so the utility stack can clear it.
   *
   * It was a hardcoded 302 in MapPage — a guess about a surface whose height
   * depends on the card strip, the trust line and whether a match row is
   * present. The sheet measured itself already, but only for drag thresholds;
   * the number never left the component, so the privacy and recenter buttons
   * sat underneath the sheet — the exact bug MapUtilityControls says it fixes.
   */
  onHeightChange,
  /**
   * This collector has a wishlist match with you.
   *
   * The pin does not say so — wishlist data never changes the marker language —
   * so the match surfaces here, attached to the person it concerns, once you
   * have chosen to look at them. It names no card: what is hidden is the whole
   * point of tapping Reveal.
   */
  isMatch = false,
  matchCopy = "",
  onRevealMatch,
}) => {
  // Read at render, never at module scope: the preference can change while the
  // app is open, and a value captured at import time could never notice.
  const reduced = prefersReducedMotion();
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const gestureRef = useRef(null);
  const sheetRef = useRef(null);
  /** Whatever had focus when the sheet opened — usually the pin — so it can be handed back. */
  const openerRef = useRef(null);

  const endGesture = useCallback(() => {
    setDragging(false);
    setDragY(0);
    gestureRef.current = null;
  }, []);

  const handlePointerDown = useCallback((e) => {
    if (!canStartDrag({ fromHandle: true })) return;
    gestureRef.current = {
      y: e.clientY,
      at: e.timeStamp,
      moved: false,
      // Captured at press time: the expand is decided on release, but the
      // question it has to answer is where the gesture BEGAN.
      blocksExpand: beganOnExpandConflictingControl(e.target),
    };
    setDragging(true);
  }, []);

  const handlePointerMove = useCallback((e) => {
    const g = gestureRef.current;
    if (!g) return;
    const dy = e.clientY - g.y;
    if (Math.abs(dy) > 4) g.moved = true;
    // Downward travel moves the sheet; upward travel does not lift it off its
    // anchor (that is what dragOffset clamps), it arms the expand instead.
    setDragY(dragOffset(dy));
  }, []);

  const handlePointerUp = useCallback(
    (e) => {
      const g = gestureRef.current;
      if (!g) {
        endGesture();
        return;
      }
      const dy = e.clientY - g.y;
      const elapsedMs = e.timeStamp - g.at;

      if (dy <= -EXPAND_DRAG_PX && !g.blocksExpand) {
        endGesture();
        if (onExpand) onExpand();
        return;
      }

      const height = sheetRef.current?.getBoundingClientRect().height || 0;
      const dismiss = shouldDismiss({
        dy,
        sheetHeight: height,
        velocity: dragVelocity({ dy, elapsedMs }),
      });
      endGesture();
      if (dismiss && onClose) onClose();
    },
    [endGesture, onClose, onExpand]
  );

  // Android hardware Back closes the preview rather than leaving the map — the
  // preview is a step INTO a collector, so Back has to be the step out of it.
  // Registered above the early return because hooks cannot be conditional, and
  // keyed on `collector` for the same reason the return below is: with nothing
  // selected there is no preview on screen for Back to dismiss.
  useDismissible(Boolean(collector), onClose, "collector-preview");

  // Measured, not guessed. ResizeObserver because the strip and the match row
  // change the height after the first paint.
  useEffect(() => {
    const node = sheetRef.current;
    if (!node || !onHeightChange) return undefined;
    const publish = () => onHeightChange(Math.round(node.getBoundingClientRect().height));
    publish();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(publish);
    ro.observe(node);
    return () => ro.disconnect();
  }, [onHeightChange, collector]);

  /**
   * Focus follows the sheet in, and follows it back out.
   *
   * Nothing moved focus when the sheet opened, so a keyboard user who activated
   * a pin was left standing on that pin: the sheet appeared in front of them and
   * Tab kept walking the REMAINING PINS behind it before it ever reached the
   * thing they had just opened — a cost that grows with however many collectors
   * happen to be on screen. The sheet ROOT takes focus rather than the close
   * button, because arriving pre-armed on Enter means the first keypress after
   * opening a preview dismisses it.
   *
   * No focus TRAP and no `aria-modal` to go with it: this sheet deliberately
   * leaves the map live behind it, so tabbing out of it is allowed.
   *
   * WHY THIS IS TWO EFFECTS AND NOT ONE
   * -----------------------------------
   * It used to be a single effect keyed on `collector`, which made SWITCHING
   * collectors — tapping a second pin while the preview is up, the commonest
   * thing anyone does on this map — run the restore. React tears the old effect
   * down before it sets the new one up, so the order was: cleanup focuses the
   * FIRST pin, then setup reads `document.activeElement` and captures that same
   * first pin as the new opener. Two consequences, both measurable:
   *
   *   - the opener never advanced. Browse A → B → C → D and Escape put you back
   *     on A's pin, however many collectors ago that was.
   *   - focus visibly jumped to the pin you had just left, mid-gesture, before
   *     landing on the sheet.
   *
   * So the two jobs are split by what they are actually keyed on. The opener is
   * a property of the preview being OPEN, so its capture/restore runs on
   * `isOpen`. Which collector is in the sheet is a property of the OCCUPANT, so
   * only the re-focus runs on that — and it re-reads the invoker, because after
   * a switch the pin owed focus back is the one just activated, not the one the
   * run started on.
   */
  const isOpen = Boolean(collector);
  // Identity, not object reference: a caller that rebuilds the collector object
  // on every render must not be able to re-steal focus with it.
  const collectorKey = collector ? String(collector._id ?? collector.id ?? "") : null;

  useEffect(() => {
    if (!isOpen) return undefined;
    return () => {
      // Hand focus back to the pin — but only if that pin is still in the
      // document. Pins re-render away as the map moves, and calling focus() on a
      // detached node silently drops focus to `<body>`, which is the very
      // failure this effect exists to prevent.
      //
      // preventScroll matters most here. A pin is a `.collector-pin` inside
      // Mapbox's `overflow:hidden` container, and MapView culls nothing —
      // `singles` is built from the whole users array with no bounds filter, so
      // a pin panned off-screen stays mounted at a position outside that
      // container. Since this sheet deliberately leaves the map pannable behind
      // it, "open a preview, pan the pin away, close" would otherwise end with
      // the browser scrolling the map to reveal the pin it is handing focus back
      // to — and mapbox never resets scrollLeft/scrollTop. jsdom ignores the
      // options argument, so no test can see this either way.
      const opener = openerRef.current;
      openerRef.current = null;
      if (opener && opener !== document.body && document.contains(opener)) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    // Whatever the user activated to get here is what focus is owed back.
    // Re-read on every switch, and ignore anything already inside the sheet:
    // on the switch commit the sheet still holds focus from the previous
    // occupant, and capturing THAT would make the sheet its own opener and
    // strand focus on a node about to be unmounted.
    const invoker = document.activeElement;
    const sheet = sheetRef.current;
    if (invoker && invoker !== document.body && !(sheet && sheet.contains(invoker))) {
      openerRef.current = invoker;
    }
    sheet?.focus({ preventScroll: true });
  }, [isOpen, collectorKey]);

  if (!collector) return null;

  const name = collectorDisplayName(collector);
  const online = isCollectorOnline(collector);
  const verified = isCollectorVerified(collector);
  const meta = collectorMetaLine(collector);
  const trust = trustCopy(collector);
  const newHere = trust === "New to Tradeloft";

  const strip = cards.slice(0, STRIP_TILES);
  const remaining = Math.max(0, (Number(totalCards) || cards.length) - strip.length);

  return (
    <div
      ref={sheetRef}
      role="dialog"
      // -1 so the root can hold focus without ever becoming a Tab stop of its
      // own; the effect above is what puts focus here.
      tabIndex={-1}
      aria-label={`${name} — collector preview`}
      data-testid="collector-preview-sheet"
      className="collector-sheet-enter map-safe-anchor"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={endGesture}
      // The map listens for pointer and click events to pan and to clear
      // selection. Both have to stop here or the sheet's own gestures reach it.
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        left: 8,
        right: 8,
        // `bottom` comes from .map-safe-anchor — it adds the safe-area inset
        // the bottom nav grows by on gesture-bar devices.
        "--map-safe-anchor": `${bottomPx}px`,
        zIndex: 35,
        padding: "12px 14px 16px",
        borderRadius: 20,
        background: "#fff",
        border: "1px solid rgba(15,23,42,0.07)",
        boxShadow: "0 -2px 10px rgba(15,23,42,0.05), 0 18px 44px rgba(15,23,42,0.20)",
        fontFamily: "Inter, sans-serif",
        color: "#0f172a",
        transform: dragY ? `translateY(${dragY}px)` : undefined,
        // Two reasons to drop the curve, and they are not the same reason.
        // While a finger is down it MUST be off or the sheet lags behind the
        // touch instead of tracking it; under reduced motion the sheet still
        // returns to rest, it just gets there without the spring-back.
        transition:
          dragging || reduced
            ? "none"
            : `transform ${MOTION.sheetSlideMs}ms cubic-bezier(0, 0, 0.2, 1)`,
        touchAction: "none",
        "--sheet-slide-ms": `${MOTION.sheetSlideMs}ms`,
      }}
    >
      {/* Drag up to open the full collection, down to dismiss. The handle is
          also the reason the sheet responds to a drag at all — a grab bar that
          does nothing trains people to distrust every other affordance. */}
      <div
        aria-hidden="true"
        style={{ width: 36, height: 4, borderRadius: 999, background: "#e2e8f0", margin: "0 auto 12px" }}
      />

      <button
        type="button"
        aria-label="Close preview"
        data-testid="collector-preview-close"
        onClick={onClose}
        style={{
          position: "absolute",
          top: 5,
          right: 5,
          width: 44,
          height: 44,
          border: "none",
          background: "none",
          padding: 0,
          color: "#64748b",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            width: 30,
            height: 30,
            borderRadius: 9,
            background: "#f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} aria-hidden="true">
            <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
          </svg>
        </span>
      </button>

      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", paddingRight: 34 }}>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <img
            src={collectorAvatarUrl(collector)}
            alt=""
            width={52}
            height={52}
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              border: "2.5px solid #fff",
              boxShadow: "0 3px 10px rgba(15,23,42,0.16)",
              objectFit: "cover",
              background: "#eef1f5",
              display: "block",
            }}
            onError={(e) => {
              const fallback = generatedAvatarUrl(name);
              if (e.target.src !== fallback) e.target.src = fallback;
            }}
          />
          {/* Labelled, as the map popup's dot is (P2-45): presence is said
              nowhere else in this sheet, so an aria-hidden dot told a screen
              reader nothing (E2E audit 19·13). */}
          <span
            role="img"
            aria-label={online ? "Online" : "Offline"}
            style={{
              position: "absolute",
              right: 0,
              bottom: 2,
              width: 12,
              height: 12,
              borderRadius: "50%",
              border: "2px solid #fff",
              background: online ? "#16a34a" : "#cbd5e1",
            }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
            <span
              data-testid="collector-preview-name"
              style={{
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <bdi>{name}</bdi>
            </span>
            {verified && (
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="#6d28d9"
                style={{ flexShrink: 0 }}
                role="img"
                aria-label="Verified collector"
              >
                <path d="M12 2l2.4 1.8 3-.2.9 2.9 2.5 1.7-1.2 2.8 1.2 2.8-2.5 1.7-.9 2.9-3-.2L12 22l-2.4-1.8-3 .2-.9-2.9-2.5-1.7L4.4 13 3.2 10.2l2.5-1.7.9-2.9 3 .2L12 2zm-1 13.4l5-5-1.4-1.4-3.6 3.6-1.6-1.6L8 12.4l3 3z" />
              </svg>
            )}
          </div>
          {/* Area only, never a number — and omitted entirely when no safe area
              resolves, rather than guessed. See collectorMetaLine. */}
          {meta && (
            <div data-testid="collector-preview-meta" style={{ fontSize: 12.5, fontWeight: 500, color: "#64748b", marginTop: 3 }}>
              {meta}
            </div>
          )}
          <div
            data-testid="collector-preview-trust"
            style={{ fontSize: 12, fontWeight: 600, marginTop: 4, color: newHere ? "#64748b" : "#16a34a" }}
          >
            {trust}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, marginBottom: 7 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: "#64748b" }}>
          In their binder
        </span>
        {!cardsLoading && totalCards > 0 && (
          <span style={{ fontSize: 11.5, fontWeight: 600, color: "#64748b" }}>
            {totalCards} public card{totalCards === 1 ? "" : "s"}
          </span>
        )}
      </div>

      <div data-testid="collector-preview-strip" style={{ display: "flex", gap: 6 }}>
        {cardsLoading
          ? Array.from({ length: STRIP_TILES }).map((_, i) => (
              <span
                key={`skeleton-${i}`}
                style={{
                  ...TILE_BOX,
                  background: "#eef1f5",
                  border: "1px solid rgba(15,23,42,0.06)",
                }}
              />
            ))
          : strip.map((item, i) => (
              <span
                key={item?._id || (item?.cardId || item)?.id || `card-${i}`}
                style={{
                  ...TILE_BOX,
                  background: "#eef1f5",
                  border: "1px solid rgba(15,23,42,0.06)",
                  overflow: "hidden",
                }}
              >
                <img
                  src={cardImageFor(item)}
                  alt=""
                  loading="lazy"
                  data-img-stage={ownerImageFor(item) ? "owner" : "catalog"}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  onError={(e) => {
                    // A card that fails to load stays a card. It used to fall
                    // back to an initials avatar, which read as a person.
                    //
                    // Now that a Vault tile leads with the owner's photo there
                    // is a rung in between: an owner photo is hosted elsewhere
                    // and can 404 while the catalogue art is fine, and #252
                    // settled that the honest substitute is THIS card's own art,
                    // not a generic stand-in. The rung is tracked in a data
                    // attribute because the DOM reports a RESOLVED absolute src,
                    // so a relative candidate never compares equal.
                    const stage = e.target.dataset.imgStage;
                    const catalog = catalogImageFor(item);
                    if (stage === "owner" && catalog) {
                      e.target.dataset.imgStage = "catalog";
                      e.target.src = catalog;
                      return;
                    }
                    if (stage === "placeholder") return;
                    e.target.dataset.imgStage = "placeholder";
                    e.target.src = CARD_PLACEHOLDER;
                  }}
                />
              </span>
            ))}
        {!cardsLoading && remaining > 0 && (
          <button
            type="button"
            data-testid="collector-preview-more"
            onClick={onExpand}
            style={{
              ...TILE_BOX,
              background: "#f8fafc",
              border: "1px dashed rgba(15,23,42,0.16)",
              fontSize: 11,
              fontWeight: 700,
              color: "#64748b",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            +{remaining}
          </button>
        )}
        {!cardsLoading && totalCards === null && (
          <div data-testid="preview-cards-failed" style={{ fontSize: 13, color: "#64748b", padding: "8px 0" }}>
            Couldn't load their cards right now.
          </div>
        )}
        {!cardsLoading && totalCards !== null && strip.length === 0 && remaining === 0 && (
          <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: "#64748b", padding: "12px 0" }}>
            No public cards yet.
          </span>
        )}
      </div>

      {isMatch && (
        <div
          data-testid="collector-preview-match"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 14,
            padding: "10px 12px",
            borderRadius: 12,
            background: "rgba(109,40,217,0.06)",
            border: "1px solid rgba(109,40,217,0.2)",
          }}
        >
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: "#4c1d95", lineHeight: 1.35 }}>
            {matchCopy || "They have something on your wishlist"}
          </span>
          <button
            type="button"
            data-testid="collector-preview-reveal-match"
            onClick={onRevealMatch}
            style={{
              flexShrink: 0,
              minHeight: 44,
              padding: "0 14px",
              border: "none",
              borderRadius: 10,
              background: "#6d28d9",
              color: "#fff",
              fontSize: 12.5,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Reveal match
          </button>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button
          type="button"
          data-testid="collector-preview-view-collection"
          onClick={onExpand}
          style={{
            flex: 1.5,
            height: 44,
            border: "none",
            borderRadius: 10,
            background: "#6d28d9",
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          View collection
        </button>
        <button
          type="button"
          data-testid="collector-preview-profile"
          onClick={onOpenProfile}
          style={{
            flex: 1,
            height: 44,
            borderRadius: 10,
            background: "#fff",
            border: "1px solid rgba(15,23,42,0.14)",
            color: "#334155",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Profile
        </button>
        <button
          type="button"
          aria-label={`Message ${name}`}
          data-testid="collector-preview-message"
          onClick={onMessage}
          disabled={messageBusy}
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: "#fff",
            border: "1px solid rgba(15,23,42,0.14)",
            color: "#334155",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: messageBusy ? "default" : "pointer",
            opacity: messageBusy ? 0.6 : 1,
            padding: 0,
          }}
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 12c0 4.418-4.03 8-9 8a9.9 9.9 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CollectorPreviewSheet;
